import React from 'react';
import styled from 'styled-components';
import { Title, colors, tabletThresholdPixels } from '../home/v4/styledComponents';
import backgroundImage from '../../assets/landingPages/background.repairClothes.jpg';
import jeanRepair from '../../assets/landingPages/jean.repairClothes.jpg';
import pullRepair from '../../assets/landingPages/pull.repairClothes.jpg';
import shoesRepair from '../../assets/landingPages/shoes.repairClothes.jpg';

type TextPropsType = {
  width?: boolean;
  monospace?: boolean;
  stroke?: boolean;
};
type TextBlockPropsType = {
  row?: boolean;
  marginTop?: boolean;
};
type BackgroundPropsType = {
  backgroundImage: string;
};

const Container = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  @media (max-width: ${tabletThresholdPixels}) {
    width: calc(100vw - 48px);
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 34px;
  margin-top: 64px;
  width: 100%;
  @media (max-width: ${tabletThresholdPixels}) {
    margin-top: 40px;
  }
`;
const TextBlock = styled.div<TextBlockPropsType>`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 63px;
  width: 100%;
  @media (max-width: ${tabletThresholdPixels}) {
    flex-direction: column-reverse;
    gap: 40px;
  }
`;
const Text = styled.p<TextPropsType>`
  margin: 0;
  font-size: 14px;
  font-family: ${(props) => props.monospace ? 'Space Mono' : 'Roboto'};
  ${(props) => props.width && 'width: 550px;'};
  ${(props) => props.stroke && 'text-decoration: line-through;'};
  @media (max-width: ${tabletThresholdPixels}) {
    ${(props) => props.width && 'width: 100%;'};
  }
`;
const Background = styled.div<BackgroundPropsType>`
  display: flex;
  width: 550px;
  box-sizing: border-box;
  padding: 64px 32px;
  align-items: flex-end;
  border-radius: 12px;
  background: url(${(props) => props.backgroundImage}) lightgray center center / cover no-repeat;
  @media (max-width: ${tabletThresholdPixels}) {
    width: 100%;
    padding: 40px 20px;
  }
`;
const ExamplesBlock = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  @media (max-width: ${tabletThresholdPixels}) {
    width: 100%;
    overflow-x: auto;
    justify-content: flex-start;
  }
`;
const Example = styled.div`
  position: relative;
`;
const ExampleText = styled.div`
  position: absolute;
  bottom: 5%;
  left: 50%;
  transform: translateX(-50%); /* Centrage horizontal */
  width: 332px;
  box-sizing: border-box;
  display: flex;
  padding: 24px;
  flex-direction: column;
  gap: 12px;
  border-radius: 8px;
  background: #FEFCF9;
  font-family: "Space Mono";
  font-size: 20px;
  @media (max-width: ${tabletThresholdPixels}) {
    width: 200px;
    padding: 12px;
    font-size: 12px;
  }
`;
const Prices = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 205px;
  height: 55px;
  gap: 10px;
  border-radius: 4px;
  color: ${colors.beige100};
  background: var(--Neutral-0, #000);
  @media (max-width: ${tabletThresholdPixels}) {
    width: 100px;
    height: 30px;
  }
`;
const Img = styled.img`
  width: 380px;
  height: 604px;
  @media (max-width: ${tabletThresholdPixels}) {
    width: 233px;
    height: 403px;
  }
`;
const RepairBonusPrice = styled.span`
  color: ${colors.yellow900};
  font-family: "Space Mono";
  font-size: 20px;
  font-weight: 700;
  text-transform: uppercase;
  line-height: 1px;
  @media (max-width: ${tabletThresholdPixels}) {
    font-size: 14px;
    line-height: 2px;
  }
`;

const examplesData = [
  {
    src: jeanRepair,
    alt: 'jean repair',
    text: 'Réparation d‘une déchirure sur un jean',
    price: '12€',
    repairBonusPrice: '7€',
  },
  {
    src: pullRepair,
    alt: 'pull repair',
    text: 'Raccommodage d‘un trou sur un pull',
    price: '12€',
    repairBonusPrice: '5€',
  },
  {
    src: shoesRepair,
    alt: 'shoes repair',
    text: 'Changement du bonbout d‘une paire de bottines',
    price: '29€',
    repairBonusPrice: '22€',
  },
];

function RepairBonusLP() {
  return (
    <Container>
      <Title>Le Bonus Réparation</Title>
      <Content>
        <TextBlock>
          <Text width>
            Adoptée en février 2020, la loi AGEC a engendré la création d‘un fonds spécifique consacré à la réparation des articles du secteur Textile,
            Linge de maison et Chaussure (TLC).
            {' '}
            <br />
            <br />
            Cette initiative, surnommée “Bonus Réparation“, vous offre l‘opportunité de profiter de réductions
            significatives, allant de 6 à 25 euros, sur les réparations de vos vêtements, votre linge de maison et vos chaussures.
            <br />
            <br />
            Cette mesure incitative a pour but de favoriser la réparation au lieu du remplacement, encourageant ainsi une démarche plus durable.
          </Text>
          <Background backgroundImage={backgroundImage}>
            <Text monospace>
              Grâce au Bonus Réparation, réparez vos jeans déchirés pour
              {' '}
              <b>seulement 5 euros</b>
              , au lieu de
              {' '}
              <b>12 euros.</b>
              <b>Le Bonus Réparation</b>
              {' '}
              rend la réparation de vêtements non seulement plus abordable, mais également plus séduisante pour tous.
              Ne manquez pas cette chance de donner une seconde vie à vos jeans préférés tout en économisant !
            </Text>
          </Background>
        </TextBlock>
        <ExamplesBlock>
          {examplesData.map((example) => (
            <Example key={example.alt}>
              <Img src={example.src} alt={example.alt} />
              <ExampleText>
                {example.text}
                <br />
                <Prices>
                  <Text stroke>{example.price}</Text>
                  {' '}
                  <RepairBonusPrice>{example.repairBonusPrice}</RepairBonusPrice>
                </Prices>
              </ExampleText>
            </Example>
          ))}
        </ExamplesBlock>
      </Content>
    </Container>
  );
}

export default RepairBonusLP;
